import { clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';
import type { ClassValue } from 'clsx';
import DOMPurify from 'dompurify';
import { toast } from 'sonner';

/**
 * Merge class names.
 * @description This is a helper function to merge class names.
 * @param inputs - The class names to merge.
 * @returns The merged class names.
 */
export function cn(...inputs: Array<ClassValue>) {
  return twMerge(clsx(inputs));
}

export const sanitizeHtml = (html: string) => {
  return DOMPurify.sanitize(html, {
    ALLOWED_TAGS: [
      'h1',
      'h2',
      'h3',
      'h4',
      'h5',
      'h6',
      'p',
      'br',
      'b',
      'i',
      'strong',
      'em',
      'u',
      'del',
      's',
      'mark',
      'a',
      'img',
      'ul',
      'ol',
      'li',
      'label',
      'input',
      'blockquote',
      'code',
      'pre',
      'hr',
      'table',
      'thead',
      'tbody',
      'tfoot',
      'tr',
      'td',
      'th',
      'span',
      'div'
    ],
    ALLOWED_ATTR: [
      'href',
      'target',
      'rel',
      'src',
      'alt',
      'title',
      'width',
      'height',
      'style',
      'class',
      'type',
      'checked',
      'disabled',
      'contenteditable',
      'data-type',
      'data-checked',
      'data-background-color',
    ],
    FORBID_TAGS: ['script', 'style', 'iframe', 'form'],
    FORBID_ATTR: ['onerror', 'onload', 'onclick'],
  });
};


export const getFormattedDate = (date: number) => {
  return new Date(date).toLocaleString('de-DE', {
    month: 'numeric',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  });
};


/**
 * Get item type based on file type.
 * @description This is a helper function to get item type based on file type.
 * @param fileType - The file type.
 * @returns The item type.
 */
export const getItemTypeBasedOnFileType = (fileType: string) => {
  if (fileType.includes('image')) return 'IMAGE';
  if (fileType.includes('video')) return 'VIDEO';
  if (fileType.includes('audio')) return 'AUDIO';
  else return 'ATTACHMENT';
};

/**
   * Show a toast message if the user is not authorized to update the item.
   */
export const showUnauthorizedToast = (type: 'update' | 'create' | 'delete' = 'update') => {
  toast.error(`You are not authorized to ${type} this item`);
};

export const sanitizeAndReplaceLinksInMessageContent = (messageContent: string) => {
  // Updated regex that won't break www. and domain
  const urlRegex =
    /(?:https?:\/\/(?:www\.)?|www\.)[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*\.[a-zA-Z]{2,}(?:\/[^\s<>"']*)?|(?<!www\.)[a-zA-Z0-9-]+\.[a-zA-Z]{2,}(?:\/[^\s<>"']*)?/g;

  // Replace URLs with anchor tags
  let sanitized = messageContent.replace(urlRegex, (url) => {
    // Add https:// if the URL doesn't start with http:// or https://
    const fullUrl = url.startsWith('http')
      ? url
      : url.startsWith('www.')
        ? `https://${url}`
        : `https://${url}`;

    // Create the anchor tag with proper attributes
    return `<a href="${fullUrl}" target="_blank" rel="noopener noreferrer" class="italic text-blue-500 hover:underline font-semibold">${url}</a>`;
  });

  // Handle existing anchor tags if any
  const links = sanitized.match(/<a href="([^"]+)">([^<]+)<\/a>/g);
  if (links) {
    links.forEach((link) => {
      const hrefMatch = link.match(/href="([^"]+)"/) ?? [];
      const textMatch = link.match(/<a href="[^"]+">([^<]+)<\/a>/) ?? [];
      if (hrefMatch[1] && textMatch[1]) {
        sanitized = sanitized.replace(
          link,
          `<a href="${hrefMatch[1]}" target="_blank" rel="noopener noreferrer" class="text-blue-500 hover:underline font-semibold">${textMatch[1]}</a>`
        );
      }
    });
  }
  return sanitizeHtml(sanitized);
};


/**
 * Capitalizes the first letter of each word in a string
 */
export const capitalizeWords = (str: string): string => {
  return str
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
}


// Works for <input>, <textarea>, and contentEditable elements.
export const handlePastePlain = (e: React.ClipboardEvent<HTMLElement>) => {
  const plain = e.clipboardData.getData('text/plain');
  const html = e.clipboardData.getData('text/html');
  // Only strip markup from the HTML fallback; plain text (e.g. JSON with `<=`) must pass through untouched.
  // The closing `>` is required (not `>?`) so a lone `<` can't greedily consume the rest of the string.
  const removeHtmlTags = (text: string) => text.replace(/<[^>]*>/g, '');
  const text = plain || removeHtmlTags(html);
  if (!text) return;

  const el = e.currentTarget as HTMLElement & {
    value?: string;
    selectionStart?: number;
    selectionEnd?: number;
    setRangeText?: (text: string, start?: number, end?: number, selectMode?: 'select'|'start'|'end'|'preserve') => void;
    isContentEditable?: boolean;
  };

  e.preventDefault();

  if ('setRangeText' in el && typeof el.setRangeText === 'function') {
    const start = el.selectionStart ?? 0;
    const end = el.selectionEnd ?? start;
    el.setRangeText(text, start, end, 'end');
    // Sync React controlled inputs after programmatic paste
    el.dispatchEvent(new Event('input', { bubbles: true }));
  } else if (el.isContentEditable) {
    document.execCommand('insertText', false, text);
  }
};
/**
 * Scroll into view if the element is out of parent container's height
 * @description This is a helper function to scroll into view if the element is out of parent container's height
 * @param element - The element to scroll into view
 */
export const scrollIntoViewIfOutOfParentContainerHeight = (element: HTMLElement | null, position: 'start' | 'center' | 'end' = 'start') => {
  if(!element) return false;
  const parentHeight = element.parentElement?.clientHeight || 0;
  const elementHeight = element.clientHeight;
  element.scrollIntoView({ behavior: 'smooth', block: position || 'start' });
  if(elementHeight && parentHeight && elementHeight > parentHeight) {
    window.scrollTo({ top: element.offsetTop, behavior: 'smooth' });
    return true;
  }
  return false;
};
